import { useContext, useEffect, useMemo, useState } from "react";
import { useVpnRequestList } from "../HelpDesk/useVpnRequestList";
import _ from "underscore";
import { dateConvert, getFullMonthSelected } from "../../../utils/Helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGitRequestList } from "../HelpDesk/useGitRequestList";
import { useJenkisRequestList } from "../HelpDesk/useJenkisRequestList";
import { useTheme } from "../../../context/ThemeContext";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { ToastContext } from "../../../context/ToastContext";
import { Calendar } from "primereact/calendar";
import { useCommon } from "../../Common/useCommon";

export const useHelpDeskReport = ({ filterValues, setFilterValues }) => {

    const { theme } = useTheme();
    const { getValuePermission } = useCommon()
    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const { dropDownOption, getRequestedData: getVpnData, rowData: vpnRowData, loader: vpnLoader } = useVpnRequestList({ fromReport: true });
    const { getRequestedData: getGitData, rowData: gitRowData, loader: gitLoader } = useGitRequestList({ fromReport: true });
    const { getRequestedData: getJenkinsData, rowData: jenkinsRowData, loader: jenkinsLoader } = useJenkisRequestList({ fromReport: true });
    const [status, setStatus] = useState("active");
    const [action, setAction] = useState({ date: null, row: {} });
    const rowData = {
        vpn: !_.isEmpty(vpnRowData) ? _.sortBy(vpnRowData, "requested_user_name") : [],
        git: _.sortBy(gitRowData, "request_by"),
        jenkins: _.sortBy(jenkinsRowData, "requested_user_name")
    }
    const loader = {
        vpn: vpnLoader,
        git: gitLoader,
        jenkins: jenkinsLoader
    }
    const columns = [
        {
            key: filterValues?.type === "git" ? "request_by" : "requested_user_name",
            label: "Employee",
            style: { width: "250px" },
            render: (row) => (
                <div className="d-flex gap-2 align-items-center">
                    <img className="rounded-circle" src={row?.image_url || "./assets/grey-user-icon.png"} width={25} height={25} onError={(e) => { e.target.onerror = null; e.target.src = "./assets/grey-user-icon.png"; }} />
                    <div className="fw-semibold">{row?.[filterValues?.type === "git" ? "request_by" : "requested_user_name"]}</div>
                </div>
            )
        },
        {
            key: "project_name",
            label: "Project",
            style: { width: filterValues?.type === "vpn" ? "200px" : "180px" },
            render: (row) => <div className="text-truncate" title={row?.project_name}>{row?.project_name}</div>
        },
        {
            key: "is_prod",
            label: "VPN Type",
            style: { width: "140px" },
            headerStyle: { textAlign: "center" },
            hide: filterValues?.type !== "vpn",
            render: (row) => (
                <div className="d-flex justify-content-center">
                    <div className="d-flex justify-content-center rounded-pill fw-semibold px-2" style={{ width: "fit-content", backgroundColor: theme === "dark" ? "#333333" : "#dfdfdf" }}>{row?.is_prod ? "PROD" : "DEV/QA"}</div>
                </div>
            )
        },
        {
            key: "repo_name",
            label: "Git Repo",
            style: { width: "300px" },
            hide: filterValues?.type !== "git",
            render: (row) => (
                <div className="text-truncate" title={row?.repo_name}>{row?.repo_name}</div>
            )
        },
        {
            key: "environment_name",
            label: "Env",
            style: { width: "130px" },
            hide: filterValues?.type !== "jenkins",
            render: (row) => <div className="text-truncate" title={row?.environment_name}>{row?.environment_name}</div>
        },
        {
            key: "jenkins_type_name",
            label: "Jenkins Type",
            style: { width: "130px" },
            hide: filterValues?.type !== "jenkins",
            render: (row) => <div className="text-truncate" title={row?.jenkins_type_name}>{row?.jenkins_type_name}</div>
        },
        {
            key: "sub_type_name",
            label: "Sub Type",
            style: { width: "100px" },
            hide: filterValues?.type !== "jenkins",
            render: (row) => <div className="text-truncate" title={row?.sub_type_name}>{row?.sub_type_name}</div>
        },
        {
            key: "job_name",
            label: "Job Name",
            style: { width: "130px" },
            hide: filterValues?.type !== "jenkins",
            render: (row) => <div className="text-truncate" title={row?.job_name?.split(",")?.join("\n")}>{row?.job_name?.split(",")?.join(", ")}</div>
        },
        {
            key: "approve_datetime",
            label: "Granted Date",
            style: { width: "115px" },
            render: (row) => <div>{row?.approve_datetime ? dateConvert(row?.approve_datetime, "day-mon-year") : ""}</div>
        },
        {
            key: "expiry_datetime",
            label: status === "active" ? "Expiry Date" : "Revoked Date",
            style: { width: "115px" },
            render: (row) => {
                let maxDate = new Date(new Date().setMonth(new Date().getMonth() + 3));
                if (status === "active") {
                    maxDate = new Date(new Date(row?.expiry_datetime).setMonth(new Date(row?.expiry_datetime).getMonth() + 3));
                }
                return (
                    _.isEqual(action?.row, row) && !_.isEmpty(action?.row) ?
                        <div className="inspectra-calendar d-flex">
                            <Calendar className="input-dd hd-report-calendar" placeholder="Select Expiry Date" value={action?.date} onChange={(e) => setAction((prev) => ({ ...prev, date: e.target.value }))} dateFormat="dd-mm-yy" minDate={new Date()} maxDate={maxDate} disabled={action?.isRevoke} />
                        </div> :
                        <div>{row?.approve_datetime ? dateConvert(row?.expiry_datetime, "day-mon-year") : ""}</div>
                )
            }
        },
        {
            key: "action",
            label: "Action",
            style: { width: "100px" },
            headerStyle: { textAlign: "center" },
            hide: getValuePermission("service_report_action_column"),
            render: (row) => (
                _.isEqual(action?.row, row) && !_.isEmpty(action?.row) ?
                    <div className="d-flex justify-content-center gap-3">
                        <div className="text-primary c-pointer" title="Modify Rights" onClick={() => handleSave(false, row)}><FontAwesomeIcon icon="fa-solid fa-check" size="lg" /></div>
                        <div className="text-danger c-pointer" title="Revoke Access" onClick={() => setAction((prev) => ({ ...prev, date: null, row: {} }))}><FontAwesomeIcon icon="fa-solid fa-close" size="lg" /></div>
                    </div> :
                    (status === "active" ?
                        <div className="d-flex justify-content-center gap-3">
                            <div className="text-primary c-pointer" title="Modify Rights" onClick={() => setAction((prev) => ({ ...prev, row, date: new Date(row?.expiry_datetime) }))}><FontAwesomeIcon icon="fa-solid fa-pencil" size="lg" /></div>
                            <div className="text-danger c-pointer" title="Revoke Access" onClick={() => handleSave(true, row)}><FontAwesomeIcon icon="fa-solid fa-hand-point-left" size="lg" /></div>
                        </div> :
                        <div className="text-center">
                            <div className="text-success c-pointer" title="Restore Access" onClick={() => setAction((prev) => ({ ...prev, row, date: new Date(row?.expiry_datetime) }))}><FontAwesomeIcon icon="fa-solid fa-arrow-rotate-left" /></div>
                        </div>)
            )
        },
    ]

    // find the status id and call GET API according to the type selected
    useEffect(() => {
        let id = 0;
        const date = getFullMonthSelected(`${filterValues?.year}-${filterValues?.month}-01`);
        if (status === "expired") {
            id = _.findWhere(dropDownOption?.statusOptions, { request_status_key: "is_expired" })?.request_status_id;
        } else {
            id = _.findWhere(dropDownOption?.statusOptions, { request_status_key: "is_grant" })?.request_status_id;
        }
        if (id && filterValues?.apiCall) {
            if (filterValues?.type === "vpn") {
                getVpnData(null, id, { ...date, projects: filterValues?.projects });
            } else if (filterValues?.type === "git") {
                getGitData(id, { ...date, projects: filterValues?.projects });
            } else if (filterValues?.type === "jenkins") {
                getJenkinsData(null, id, { ...date, projects: filterValues?.projects });
            }
            setAction((prev) => ({ ...prev, date: null, row: {} }));
            setFilterValues((prev) => ({ ...prev, apiCall: false, isChange: false }));
        }
    }, [dropDownOption, filterValues?.apiCall])

    // common update expiry date API for all 3 types
    const handleSave = async (isRevoke, row) => {
        try {
            if (!isRevoke && !action?.date) {
                showInfo("Info", "Please select an expiry date");
                return;
            } else if (dateConvert(row?.expiry_datetime) === dateConvert(action?.date)) {
                showInfo("Info", "No changes made");
                return;
            }
            const payload = {
                request_type: filterValues?.type || "",
                request_id: row?.[filterValues?.type === "vpn" ? "vpn_request_id" : filterValues?.type === "git" ? "git_repo_request_id" : "jenkins_request_id"] || 0,
                request_datetime: dateConvert(isRevoke ? new Date() : action?.date, "iso-string") || "",
                request_status_id: _.findWhere(dropDownOption?.statusOptions, { request_status_key: isRevoke ? "is_expired" : "is_grant" })?.request_status_id || 0
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTSERVICEREQUESTEXTEND"), payload);
            if (!res?.error_status) {
                showSuccess("Success", res?.body?.message);
                setFilterValues((prev) => ({ ...prev, apiCall: true }))
            }
        } catch (error) {
            console.error("Error: ", error);
            error?.response?.data?.errorMessage && showError("Error", error?.response?.data?.errorMessage);
        }
    }

    return useMemo(() => ({
        status, setStatus, columns, loader, rowData
    }), [status, setStatus, columns, loader, rowData])
}